import React, { FC, useEffect } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { BEIGE_300, BEIGE_600, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { MUISwitch } from 'components/common/MUISwitch'
import { PfRevampEventName, usePfRevampAnalytics } from '../../../../analytics'
import creativeOne from 'assets/img/creatives/creative-1.webp'
import creativeTwo from 'assets/img/creatives/creative-2.webp'
import creativeThree from 'assets/img/creatives/creative-3.webp'
import { NoFavouriteCreativesHint } from './NoFavouriteCreativesHint.component'
import { usePfSubscriptionEntitlement, usePfSubscriptionEntitlementKnown } from '../../usePfSubscriptionEntitlement'

/** The three portraits, left to right; the middle one is larger and sits on top. */
const AVATARS = [
  { src: creativeOne, size: '4rem', left: 0, zIndex: 1 },
  { src: creativeTwo, size: '4.8rem', left: '2.8rem', zIndex: 2 },
  { src: creativeThree, size: '4rem', left: '6.4rem', zIndex: 1 },
]

interface Props {
  checked: boolean
  onToggle: (checked: boolean) => void
  /** The client has no favourite creative yet: the switch is shown off and disabled, with how to get one. */
  noFavourites?: boolean
}

/** Set once the client has seen the free-plan announcement, so later visits show the plain switch. */
const ANNOUNCED_STORAGE_KEY = 'pf-choose-creative-announced'

/** Whether this is the client's first sight of the option in this browser; recorded as seen right away. */
const takeFirstDisplay = (): boolean => {
  try {
    if (window.localStorage.getItem(ANNOUNCED_STORAGE_KEY)) return false
    window.localStorage.setItem(ANNOUNCED_STORAGE_KEY, '1')
    return true
  }
  catch {
    // Storage unavailable (private mode): the plain switch, rather than the announcement on every visit.
    return false
  }
}

/**
 * Decided once per page load and shared by both booking panels: switching fixed ↔ flexible remounts the switch,
 * and the announcement must not vanish while the client is still on the page.
 */
let announcementThisPage: boolean | undefined
let announcementTracked = false
const isAnnouncementThisPage = (): boolean => {
  if (announcementThisPage === undefined) announcementThisPage = takeFirstDisplay()
  return announcementThisPage
}

/** Test seam: forget this page's decision, as a new page load would. */
export const resetCreativeAnnouncementForTests = () => {
  announcementThisPage = undefined
  announcementTracked = false
}

/**
 * Switch that lets the client pick the creative themselves. The first time it is shown it is announced as now open
 * to every plan (card with portraits); afterwards it is a plain switch. The whole row is the switch's label, so a
 * click anywhere on it toggles.
 */
export const ChooseCreativeToggle: FC<Props> = ({ checked, onToggle, noFavourites = false }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  // The announcement is news only on the free plan: a workspace with an active subscription always had the
  // option (QA-667). The portraits would also promote creatives the client cannot pick, so it waits for a
  // visit with some. Checked before the first display is recorded, so neither case uses it up.
  const isSubscribed = usePfSubscriptionEntitlement()
  // Only once the plan is known: before the workspace resolves, or on a resumed order placed on behalf whose
  // client plan is not stored, "not subscribed" is a guess, and a paying client would use up the announcement.
  const isPlanKnown = usePfSubscriptionEntitlementKnown()
  const isAnnouncement = !noFavourites && isPlanKnown && !isSubscribed && isAnnouncementThisPage()
  const trackAnalytics = usePfRevampAnalytics()

  useEffect(() => {
    if (!isAnnouncement || announcementTracked) return
    announcementTracked = true
    trackAnalytics({ type: PfRevampEventName.CREATIVE_CHOICE_ANNOUNCED, payload: {} })
  }, [isAnnouncement, trackAnalytics])

  if (!isAnnouncement) return (
    <Stack gap={1.2}>
    <Box data-cy="pf-revamp-choose-creative-toggle" sx={{ '& .MuiFormControlLabel-root': { alignItems: 'flex-start', gap: 1.2, m: 0 } }}>
      <MUISwitch
        checked={checked && !noFavourites}
        disabled={noFavourites}
        onChange={() => onToggle(!checked)}
        labelPlacement="end"
        label={
          <Stack minWidth={0}>
            <Typography variant="text-md" fontWeight={550} lineHeight="2.4rem" color={noFavourites ? GRAY_700 : GRAY_900}>
              {t('purchase_flow_revamp:instructions.schedule.choose_favorite_creative')}
            </Typography>
            {/* Without favourites the card below explains it; the switch keeps only its title. */}
            {!noFavourites &&
              <Typography variant="text-sm" fontWeight={350} lineHeight="2rem" color={GRAY_700}>
                {t('purchase_flow_revamp:instructions.schedule.transport_fees_hint')}
              </Typography>
            }
          </Stack>
        }
      />
    </Box>
    {/* No favourite yet: say how to get one rather than only that there is none. */}
    {noFavourites && <NoFavouriteCreativesHint />}
    </Stack>
  )

  return (
    <Box
      data-cy="pf-revamp-choose-creative-toggle"
      sx={{
        border: `1px solid ${BEIGE_600}`,
        borderRadius: '0.8rem',
        backgroundColor: BEIGE_300,
        // The switch leads, like every other control in the flow; the portraits close the row.
        '& .MuiFormControlLabel-root': { width: '100%', justifyContent: 'flex-start', gap: 1.6, p: 1.6, m: 0, boxSizing: 'border-box' },
        '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
      }}
    >
      <MUISwitch
        checked={checked}
        onChange={() => onToggle(!checked)}
        labelPlacement="end"
        label={
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.6}>

            <Stack minWidth={0}>
              <Typography variant="text-md" fontWeight={550} lineHeight="2.4rem" color={GRAY_900}>
                {t('purchase_flow_revamp:instructions.schedule.choose_creative_free_tier_title')}
              </Typography>
              <Typography variant="text-sm" fontWeight={350} lineHeight="2rem" color={GRAY_700}>
                {t('purchase_flow_revamp:instructions.schedule.transport_fees_hint')}
              </Typography>
            </Stack>

            <Box aria-hidden sx={{ position: 'relative', flexShrink: 0, width: '10.4rem', height: '5.2rem', display: { xs: 'none', sm: 'block' } }}>
              {AVATARS.map(({ src, size, left, zIndex }) => (
                <Box
                  key={src}
                  component="img"
                  src={src}
                  alt=""
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left,
                    zIndex,
                    width: size,
                    height: size,
                    transform: 'translateY(-50%)',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: `2px solid ${WHITE}`,
                    boxShadow: '0 1px 4px rgba(19, 24, 29, 0.1)',
                  }}
                />
              ))}
            </Box>

          </Stack>
        }
      />
    </Box>
  )
}
